/*
 * https://github.com/morethanwords/tweb
 * Copyright (C) 2019-2021 Eduard Kuzmenko
 * https://github.com/morethanwords/tweb/blob/master/LICENSE
 *
 * Originally from:
 * https://github.com/zhukov/webogram
 * Copyright (C) 2014 Igor Zhukov <igor.beatle@gmail.com>
 * https://github.com/zhukov/webogram/blob/master/LICENSE
 */

import Modes from '../config/modes';
import {IS_WORKER} from '../helpers/context';
import makeError from '../helpers/makeError';
import {WorkerTaskTemplate} from '../types';
import {UserAuth} from './mtproto/mtproto_config';
import MTProtoMessagePort from './mtproto/mtprotoMessagePort';
// import { stringify } from '../helpers/json';

class LocalStorage<Storage extends Record<string, any>> {
  private prefix = '';
  private cache: Partial<Storage> = {};
  private useStorage = true;

  constructor(/* private preserveKeys: (keyof Storage)[] */) {
    if(Modes.test) {
      this.prefix = 't_';
    }
  }

  public get<T extends keyof Storage>(key: T, useCache = true): Storage[T] {
    if(this.cache.hasOwnProperty(key) && useCache) {
      return this.cache[key];
    } else if(this.useStorage) {
      let value: Storage[T];
      try {
        value = localStorage.getItem(this.prefix + (key as string)) as any;
      } catch(err) {
        this.useStorage = false;
        throw makeError('STORAGE_OFFLINE');
      }

      if(value !== null) {
        try {
          value = JSON.parse(value);
        } catch(err) {
          // console.error(err);
        }
      } else {
        value = undefined;
      }

      return value;
    } else {
      throw makeError('STORAGE_OFFLINE');
    }
  }

  public set(obj: Partial<Storage>, onlyLocal = false) {
    let lastError: any;
    for(const key in obj) {
      if(obj.hasOwnProperty(key)) {
        const value = obj[key];
        this.cache[key] = value;

        if(!onlyLocal) {
          try {
            if(!this.useStorage) {
              throw makeError('STORAGE_OFFLINE');
            }

            const stringified = JSON.stringify(value);
            localStorage.setItem(this.prefix + key, stringified);
          } catch(err) {
            this.useStorage = false;
            lastError = err;
          }
        }
      }
    }

    if(lastError) {
      throw lastError;
    }
  }

  public delete(key: keyof Storage, saveLocal = false) {
    // ! it is needed here
    key = '' + (key as string);

    if(!saveLocal) {
      delete this.cache[key];
    }

    // if(this.useStorage) {
    try {
      localStorage.removeItem(this.prefix + (key as string));
    } catch(err) {

    }
    // }
  }

  /* public clear(preserveKeys: (keyof Storage)[] = this.preserveKeys) {
    // if(this.useStorage) {
      try {
        let obj: Partial<Storage> = {};
        if(preserveKeys) {
          preserveKeys.forEach((key) => {
            const value = this.get(key);
            if(value !== undefined) {
              obj[key] = value;
            }
          });
        }

        localStorage.clear();

        if(preserveKeys) {
          this.set(obj);
        }
      } catch(err) {

      }
    // }
  } */

  public clear() {
    const keys: string[] = ['dc', 'server_time_offset', 'xt_instance', 'user_auth', 'state_id', 'k_build'];
    for(let i = 1; i <= 5; ++i) {
      keys.push(`dc${i}_server_salt`);
      keys.push(`dc${i}_auth_key`);
      keys.push(`dc${i}_hash`); // only for WebA
    }

    for(const key of keys) {
      this.delete(key, true);
    }
  }

  public toggleStorage(enabled: boolean, clearWrite: boolean) {
    this.useStorage = enabled;

    if(!clearWrite) {
      return;
    }

    if(!enabled) {
      this.clear();
    } else {
      return this.set(this.cache);
    }
  }
}

export interface LocalStorageProxyTask extends WorkerTaskTemplate {
  type: 'localStorageProxy',
  payload: {
    type: 'set' | 'get' | 'delete' | 'clear' | 'toggleStorage' | 'getCurrentUserId',
    args: any[]
  }
};

export interface LocalStorageProxyTaskResponse extends WorkerTaskTemplate {
  type: 'localStorageProxy',
  payload: any
};

export default class LocalStorageController<Storage extends Record<string, any>> {
  private static STORAGES: LocalStorageController<any>[] = [];
  // private log = (...args: any[]) => console.log('[SW LS]', ...args);
  // private log = (...args: any[]) => {};

  private storage: LocalStorage<Storage>;

  constructor(/* private preserveKeys: (keyof Storage)[] = [] */) {
    LocalStorageController.STORAGES.push(this);

    if(!IS_WORKER) {
      this.storage = new LocalStorage(/* preserveKeys */);
    }
  }

  private _userId = 0;
  updateUserIdCache() {
    try {
      this._userId = this.getCurrentUserIdSync();
    } catch{
      this.getCurrentUserId().then(v => {
        this._userId = v;
      });
    }
  }
  async init() {
    if(typeof window === 'undefined') {
      return;
    }
    this.updateUserIdCache();
    const cacheId = this.getCurrentUserLocalIdSync();
    navigator.serviceWorker.getRegistrations().then((v) => v.forEach((v) => v.active.postMessage({cacheId})));
    const {default: apiManagerProxy} = await import('./mtproto/mtprotoworker');
    if(apiManagerProxy.worker) {
      if('port' in apiManagerProxy.worker) {
        apiManagerProxy.worker.port.start();
        apiManagerProxy.worker.port.postMessage({type:'setCacheId', payload: cacheId});
      } else {
        apiManagerProxy.worker.postMessage({type:'setCacheId', payload: cacheId});
      }
    }
  }

  async getCurrentUserId() {
    const userAuth: UserAuth = await this.get('user_auth');
    return userAuth?.id ?? 0;
  }

  async getCurrentUserLocalId() {
    const users = await this.listAllUsersAsync();
    if(!users.length) {
      return 0;
    }
    const userAuth:UserAuth= await this.get('user_auth');
    const currUserIdx = users.findIndex((v) => v.user_auth?.id == userAuth?.id);
    if(currUserIdx != -1) {
      return currUserIdx;
    }
    return users.length;
  }

  getCurrentUserLocalIdSync() {
    const users = this.listUsers(true);
    if(!users.length) {
      return 0;
    }
    const userAuth: UserAuth = JSON.parse(localStorage.getItem('user_auth'));
    const currUserIdx = users.findIndex((v) => v.user_auth?.id == userAuth?.id);
    if(currUserIdx != -1) {
      return currUserIdx;
    }
    return users.length;
  }

  getCurrentUserIdSync() {
    const userAuth: UserAuth = JSON.parse(localStorage.getItem('user_auth'));
    return userAuth?.id ?? this._userId;
  }

  updateUserList() {
    let users = this.listUsers(true);
    const currentUser = this.getCurrentUserIdSync();
    const snapshot = this.getSnapshot();
    for(const [i, user] of users.entries()) {
      if(user.user_auth?.id == currentUser) {
        users[i] = snapshot;
      }
    }
    users = users.filter((v) => v.user_auth);
    if(currentUser && !users.find((v) => v.user_auth.id == currentUser)) {
      users.push(snapshot);
    }
    localStorage.setItem('users', JSON.stringify(users));
  }

  removeThisUser() {
    localStorage.setItem('users', JSON.stringify(this.listUsers(false)));
  }

  async listAllUsersAsync() {
    let users = new Array<Partial<Storage>>();
    try {
      users = await this.proxy<Partial<Storage>[]>('get', 'users');
      users ||= [];
    } catch{
    }
    return users;
  }

  listUsers(all = false) {
    if(IS_WORKER) {
      throw new Error('Method not available in workers');
    }
    let users = new Array<Partial<Storage>>();
    const currentUser = this.getCurrentUserIdSync();
    try {
      users = JSON.parse(localStorage.getItem('users'));
      users ||= [];
    } catch{
    }
    if(!all) {
      users = users.filter((v) => v.user_auth?.id != currentUser);
    }
    return users;
  }

  private async proxy<T>(type: LocalStorageProxyTask['payload']['type'], ...args: LocalStorageProxyTask['payload']['args']): Promise<T> {
    if(IS_WORKER) {
      const port = MTProtoMessagePort.getInstance<false>();
      return port.invoke('localStorageProxy', {type, args});
    }

    args = Array.prototype.slice.call(args);

    // @ts-ignore
    return this.storage[type].apply(this.storage, args as any);
  }

  public get<T extends keyof Storage>(key: T, useCache?: boolean) {
    return this.proxy<Storage[T]>('get', key, useCache);
  }

  public set(obj: Partial<Storage>, onlyLocal?: boolean) {
    return this.proxy<void>('set', obj, onlyLocal);
  }

  public delete(key: keyof Storage, saveLocal?: boolean) {
    return this.proxy<void>('delete', key, saveLocal);
  }

  public clear(/* preserveKeys?: (keyof Storage)[] */) {
    return this.proxy<void>('clear'/* , preserveKeys */);
  }

  public toggleStorage(enabled: boolean, clearWrite: boolean) {
    return this.proxy<void>('toggleStorage', enabled, clearWrite);
  }

  importSnapshot(snapshot: Partial<Storage>) {
    if(IS_WORKER) {
      throw new Error('Method not available in workers');
    }
    for(const [k, v] of Object.entries(snapshot)) {
      localStorage.setItem(k, JSON.stringify(v));
    }
  }

  private getNoProxy(key: string) {
    return JSON.parse(localStorage.getItem(key));
  }
  getSnapshot(): Partial<Storage> {
    return {
      dc:  this.getNoProxy('dc'),
      user_auth: this.getNoProxy('user_auth'),
      state_id: this.getNoProxy('state_id'),
      dc1_auth_key: this.getNoProxy('dc1_auth_key'),
      dc2_auth_key: this.getNoProxy('dc2_auth_key'),
      dc3_auth_key: this.getNoProxy('dc3_auth_key'),
      dc4_auth_key: this.getNoProxy('dc4_auth_key'),
      dc5_auth_key: this.getNoProxy('dc5_auth_key'),
      dc1_server_salt: this.getNoProxy('dc1_server_salt'),
      dc2_server_salt: this.getNoProxy('dc2_server_salt'),
      dc3_server_salt: this.getNoProxy('dc3_server_salt'),
      dc4_server_salt: this.getNoProxy('dc4_server_salt'),
      dc5_server_salt: this.getNoProxy('dc5_server_salt'),
      auth_key_fingerprint: this.getNoProxy('auth_key_fingerprint')
    } as unknown as Partial<Storage>
  }

  clearThisUser() {
    if(IS_WORKER) {
      throw new Error('Method not available in workers');
    }
    const users = localStorage.getItem('users');
    const localId = this.getCurrentUserLocalIdSync();
    localStorage.clear();
    localStorage.setItem('users', users);
    localStorage.setItem('lastLocalId', localId + '');
  }
}
